import { UserInputError } from "./errors-CEDWDXew.js";
import { isNumericId, validateResourceId, validateSlugOrId } from "./slug-validation-MJ3jU0Hd.js";
import { ParamOrganizationSlug, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { resolveScopedOrganizationSlug, resolveScopedProjectSlugOrId } from "./url-scope-CRM5sNo7.js";
import { resolveRegionUrlForOrganization } from "./resolve-region-url-DutLwiPb.js";
import { isProfileUrl, parseSentryUrl } from "./url-helpers-CcAcmPuu.js";
import { formatProfileChunkAnalysis, formatTransactionProfileAnalysis } from "./formatter-UsEdX-DU.js";
import { z } from "zod";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/get-profile-details.ts
function resolveProfileDetailsParams(params) {
	if (params.profileUrl) {
		if (!isProfileUrl(params.profileUrl)) throw new UserInputError("Invalid profile URL. URL must point to a Sentry profile resource.");
		const parsed = parseSentryUrl(params.profileUrl);
		if (parsed.type !== "profile" || !parsed.projectSlugOrId) throw new UserInputError("Invalid profile URL. URL must point to a Sentry profile resource.");
		if (parsed.profileId) return {
			mode: "transaction",
			organizationSlug: resolveScopedOrganizationSlug({
				resourceLabel: "Profile",
				scopedOrganizationSlug: params.organizationSlug,
				urlOrganizationSlug: parsed.organizationSlug
			}),
			projectSlugOrId: resolveScopedProjectSlugOrId({
				resourceLabel: "Profile",
				scopedProjectSlugOrId: params.projectSlugOrId,
				urlProjectSlug: parsed.projectSlugOrId
			}),
			profileId: parsed.profileId
		};
		if (parsed.profilerId && parsed.start && parsed.end) return {
			mode: "continuous",
			organizationSlug: resolveScopedOrganizationSlug({
				resourceLabel: "Profile",
				scopedOrganizationSlug: params.organizationSlug,
				urlOrganizationSlug: parsed.organizationSlug
			}),
			projectSlugOrId: resolveScopedProjectSlugOrId({
				resourceLabel: "Profile",
				scopedProjectSlugOrId: params.projectSlugOrId,
				urlProjectSlug: parsed.projectSlugOrId
			}),
			profilerId: parsed.profilerId,
			start: parsed.start,
			end: parsed.end
		};
		throw new UserInputError("Continuous profile URLs must include `profilerId`, `start`, and `end` query parameters.");
	}
	const hasTransactionInput = Boolean(params.profileId);
	const hasAnyContinuousInput = Boolean(params.profilerId || params.start || params.end);
	if (hasTransactionInput && hasAnyContinuousInput) throw new UserInputError("Provide either `profileId` for a transaction profile or `profilerId` + `start` + `end` for a continuous profile, not both.");
	if (!params.organizationSlug || !params.projectSlugOrId) throw new UserInputError("Provide either `profileUrl` or both `organizationSlug` and `projectSlugOrId`.");
	if (params.profileId) return {
		mode: "transaction",
		organizationSlug: params.organizationSlug,
		projectSlugOrId: params.projectSlugOrId,
		profileId: params.profileId
	};
	if (params.profilerId && params.start && params.end) return {
		mode: "continuous",
		organizationSlug: params.organizationSlug,
		projectSlugOrId: params.projectSlugOrId,
		profilerId: params.profilerId,
		start: params.start,
		end: params.end
	};
	throw new UserInputError("Provide either `profileId` for a transaction profile, or `profilerId`, `start`, and `end` for a continuous profile.");
}
async function resolveProjectContext(apiService, organizationSlug, projectSlugOrId, options = {}) {
	const requireNumericId = options.requireNumericId ?? false;
	const isNumericProject = typeof projectSlugOrId === "number" || isNumericId(String(projectSlugOrId));
	if (!isNumericProject && !requireNumericId) return {
		projectId: projectSlugOrId,
		projectSlug: String(projectSlugOrId)
	};
	const project = await apiService.getProject({
		organizationSlug,
		projectSlugOrId: String(projectSlugOrId)
	});
	return {
		projectId: project.id,
		projectSlug: project.slug
	};
}
var get_profile_details_default = defineTool({
	name: "get_profile_details",
	skills: ["inspect"],
	requiredScopes: ["event:read"],
	requiredCapabilities: ["profiles"],
	hideInExperimentalMode: true,
	description: [
		"Inspect a specific Sentry profile in detail.",
		"",
		"USE THIS TOOL WHEN:",
		"- User shares a transaction profile URL and wants the details",
		"- User has a profile ID and wants a concise summary plus raw sample structure",
		"- User needs to inspect a continuous profile session by profiler ID and time range",
		"",
		"RETURNS:",
		"- Transaction profile summary with profile URL, transaction, trace, release, and runtime details",
		"- Sample structure summaries such as frame count, sample count, stacks, and thread breakdown",
		"- Top frames by occurrence for a quick hotspot overview",
		"",
		"NOTE: This tool supports two profile modes.",
		"- Transaction profiles: pass `profileUrl` or `organizationSlug` + `projectSlugOrId` + `profileId`",
		"- Continuous profiles: pass `profileUrl` or `organizationSlug` + `projectSlugOrId` + `profilerId` + `start` + `end`",
		"",
		"<examples>",
		"### Transaction profile URL",
		"```",
		"get_profile_details(",
		"  profileUrl='https://my-org.sentry.io/explore/profiling/profile/backend/cfe78a5c892d4a64a962d837673398d2/flamegraph/'",
		")",
		"```",
		"",
		"### Transaction profile by ID",
		"```",
		"get_profile_details(",
		"  organizationSlug='my-org',",
		"  projectSlugOrId='backend',",
		"  profileId='cfe78a5c892d4a64a962d837673398d2'",
		")",
		"```",
		"",
		"### Continuous profile by session",
		"```",
		"get_profile_details(",
		"  organizationSlug='my-org',",
		"  projectSlugOrId='backend',",
		"  profilerId='041bde57b9844e36b8b7e5734efae5f7',",
		"  start='2024-01-01T00:00:00Z',",
		"  end='2024-01-01T01:00:00Z'",
		")",
		"```",
		"</examples>"
	].join("\n"),
	inputSchema: {
		profileUrl: z.string().url().optional().describe("Sentry transaction profile or continuous profile URL. If provided, organization, project, and profile identifiers are extracted from the URL."),
		organizationSlug: ParamOrganizationSlug.optional(),
		regionUrl: ParamRegionUrl.nullable().default(null),
		projectSlugOrId: z.union([z.string().trim().superRefine(validateSlugOrId), z.number()]).optional().describe("Project slug or numeric ID"),
		profileId: z.string().trim().superRefine(validateResourceId).optional().describe("Transaction profile ID from a profile flamegraph URL"),
		profilerId: z.string().trim().superRefine(validateResourceId).optional().describe("Continuous profiler session ID"),
		start: z.string().trim().optional().describe("Continuous profile start time in ISO 8601 format, for example '2024-01-01T00:00:00Z'"),
		end: z.string().trim().optional().describe("Continuous profile end time in ISO 8601 format, for example '2024-01-01T01:00:00Z'"),
		focusOnUserCode: z.boolean().default(true).describe("Show only user code frames in the hotspot table. Set to false to include library frames.")
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	async handler(params, context) {
		const resolved = resolveProfileDetailsParams({
			profileUrl: params.profileUrl,
			organizationSlug: params.organizationSlug,
			projectSlugOrId: params.projectSlugOrId,
			profileId: params.profileId,
			profilerId: params.profilerId,
			start: params.start,
			end: params.end
		});
		const regionUrl = await resolveRegionUrlForOrganization({
			context,
			organizationSlug: resolved.organizationSlug,
			regionUrl: params.regionUrl
		});
		const apiService = apiServiceFromContext(context, { regionUrl: regionUrl ?? void 0 });
		setTag("organization.slug", resolved.organizationSlug);
		if (resolved.mode === "transaction") {
			setTag("profile.id", resolved.profileId);
			const isNumericProjectInput = typeof resolved.projectSlugOrId === "number" || isNumericId(String(resolved.projectSlugOrId));
			let projectSlug$1;
			let profile;
			if (isNumericProjectInput) {
				const [project, fetchedProfile] = await Promise.all([apiService.getProject({
					organizationSlug: resolved.organizationSlug,
					projectSlugOrId: String(resolved.projectSlugOrId)
				}), apiService.getTransactionProfile({
					organizationSlug: resolved.organizationSlug,
					projectSlugOrId: resolved.projectSlugOrId,
					profileId: resolved.profileId
				})]);
				projectSlug$1 = project.slug;
				profile = fetchedProfile;
			} else {
				projectSlug$1 = String(resolved.projectSlugOrId);
				profile = await apiService.getTransactionProfile({
					organizationSlug: resolved.organizationSlug,
					projectSlugOrId: resolved.projectSlugOrId,
					profileId: resolved.profileId
				});
			}
			setTag("project.slug", projectSlug$1);
			const profileUrl$1 = params.profileUrl ?? apiService.getProfileUrl(resolved.organizationSlug, projectSlug$1, resolved.profileId);
			const traceUrl = profile.transaction?.trace_id ? apiService.getTraceUrl(resolved.organizationSlug, profile.transaction.trace_id) : void 0;
			return formatTransactionProfileAnalysis(profile, {
				focusOnUserCode: params.focusOnUserCode,
				profileUrl: profileUrl$1,
				projectSlug: projectSlug$1,
				traceUrl
			});
		}
		setTag("profiler.id", resolved.profilerId);
		const { projectId, projectSlug } = await resolveProjectContext(apiService, resolved.organizationSlug, resolved.projectSlugOrId, { requireNumericId: true });
		setTag("project.slug", projectSlug);
		setTag("project.id", String(projectId));
		const chunk = await apiService.getProfileChunk({
			organizationSlug: resolved.organizationSlug,
			profilerId: resolved.profilerId,
			projectId,
			start: resolved.start,
			end: resolved.end
		});
		const profileUrl = params.profileUrl ?? apiService.getContinuousProfileUrl(resolved.organizationSlug, projectSlug, {
			profilerId: resolved.profilerId,
			start: resolved.start,
			end: resolved.end
		});
		const chunkAnalysis = formatProfileChunkAnalysis(chunk, { focusOnUserCode: params.focusOnUserCode }).replace("# Profile Chunk Details", "## Raw Sample Analysis");
		return [
			`# Continuous Profile ${resolved.profilerId}`,
			"",
			"## Summary",
			`- **Profile URL**: ${profileUrl}`,
			`- **Project**: ${projectSlug}`,
			`- **Profiler ID**: ${resolved.profilerId}`,
			`- **Time Range**: ${resolved.start} to ${resolved.end}`,
			`- **Platform**: ${chunk.platform}`,
			`- **Release**: ${chunk.release}`,
			chunk.environment ? `- **Environment**: ${chunk.environment}` : "",
			"",
			chunkAnalysis
		].filter(Boolean).join("\n");
	}
});

//#endregion
export { get_profile_details_default };
//# sourceMappingURL=get-profile-details-B7TCia0B.js.map